'use strict';

/**
 * Lightweight UI internationalisation for Connect frontends.
 *
 * Locale files live in `lib/i18n/locales/` — edit those to change copy.
 * Browser locale comes from `navigator.languages` / `navigator.language`.
 *
 * Usage (frontend):
 *   const { t } = require('../../i18n');
 *   t('login.pitchBody')
 *
 * Usage (tests / explicit locale):
 *   t('login.pitchBody', { locale: 'en-US' })
 *   t('login.continueAs', { values: { email: 'a@b.com' }, locale: 'en-AU' })
 */

const { locales, DEFAULT_LOCALE, SUPPORTED_LOCALES } = require('./locales');

// Bare language tag → default catalog (`es` → Spain, `de` → Germany).
const LANGUAGE_DEFAULTS = Object.freeze({
  en: 'en-US',
  es: 'es-ES',
  de: 'de-DE'
});

// Per-language region overrides when the exact tag is not registered.
// English: Commonwealth spelling → en-AU; US/Canada → en-US.
// Spanish: Latin America / US → es-US; Spain → es-ES.
// German: AT/CH share de-DE copy for now.
const REGION_BY_LANGUAGE = Object.freeze({
  en: Object.freeze({
    US: 'en-US',
    CA: 'en-US',
    AU: 'en-AU',
    GB: 'en-AU',
    NZ: 'en-AU',
    IE: 'en-AU',
    ZA: 'en-AU'
  }),
  es: Object.freeze({
    US: 'es-US',
    MX: 'es-US',
    AR: 'es-US',
    CO: 'es-US',
    CL: 'es-US',
    PE: 'es-US',
    VE: 'es-US',
    EC: 'es-US',
    GT: 'es-US',
    CU: 'es-US',
    DO: 'es-US',
    HN: 'es-US',
    SV: 'es-US',
    NI: 'es-US',
    CR: 'es-US',
    PA: 'es-US',
    BO: 'es-US',
    PY: 'es-US',
    UY: 'es-US',
    PR: 'es-US',
    419: 'es-US', // UN Latin America / Caribbean
    ES: 'es-ES'
  }),
  de: Object.freeze({
    DE: 'de-DE',
    AT: 'de-DE',
    CH: 'de-DE'
  })
});

// Back-compat export used by older tests / callers.
const REGION_LOCALE = REGION_BY_LANGUAGE.en;

const lookupPath = (messages, path) => {
  if (!messages || typeof path !== 'string' || !path) return undefined;
  return path.split('.').reduce((node, key) => {
    if (node == null || typeof node !== 'object') return undefined;
    return node[key];
  }, messages);
};

const interpolate = (template, values) => {
  if (!values || typeof template !== 'string') return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(values, key) && values[key] != null) {
      return String(values[key]);
    }
    return match;
  });
};

/**
 * Normalise a BCP 47-ish tag (`en_US` → `en-US`).
 * @param {*} tag
 * @returns {string|null}
 */
const normaliseTag = (tag) => {
  if (typeof tag !== 'string') return null;
  const trimmed = tag.trim().replace(/_/g, '-');
  if (!trimmed) return null;
  const parts = trimmed.split('-').filter(Boolean);
  if (parts.length === 0) return null;
  const language = parts[0].toLowerCase();
  if (parts.length === 1) return language;
  const region = parts[1].toUpperCase();
  return `${language}-${region}`;
};

/**
 * Pick the best supported locale from a preference list
 * (e.g. `navigator.languages`).
 *
 * @param {string|string[]|null|undefined} preferred
 * @returns {string} one of SUPPORTED_LOCALES
 */
const resolveLocale = (preferred) => {
  const candidates = Array.isArray(preferred)
    ? preferred
    : preferred != null
      ? [preferred]
      : [];

  for (const raw of candidates) {
    const tag = normaliseTag(raw);
    if (!tag) continue;

    if (locales[tag]) return tag;

    const lowerMatch = SUPPORTED_LOCALES.find((id) => id.toLowerCase() === tag.toLowerCase());
    if (lowerMatch) return lowerMatch;

    const [language, region] = tag.split('-');
    const regionMap = REGION_BY_LANGUAGE[language];
    if (region && regionMap && regionMap[region] && locales[regionMap[region]]) {
      return regionMap[region];
    }

    // Language-only tag (`es`, `de`, `en`) or unknown region for that language.
    if (LANGUAGE_DEFAULTS[language] && locales[LANGUAGE_DEFAULTS[language]]) {
      return LANGUAGE_DEFAULTS[language];
    }

    const languageMatch = SUPPORTED_LOCALES.find((id) => id.toLowerCase().startsWith(`${language}-`));
    if (languageMatch) return languageMatch;
  }

  return DEFAULT_LOCALE;
};

/**
 * Read preferred languages from the browser. Empty outside a browser.
 * @returns {string[]}
 */
const detectBrowserLocales = () => {
  if (typeof navigator === 'undefined') return [];
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return navigator.languages.slice();
  }
  if (typeof navigator.language === 'string' && navigator.language) {
    return [navigator.language];
  }
  return [];
};

/**
 * Resolve the active UI locale from the browser (or an override).
 * @param {string|string[]|null|undefined} override
 * @returns {string}
 */
const getActiveLocale = (override) => {
  if (override != null) return resolveLocale(override);
  return resolveLocale(detectBrowserLocales());
};

/**
 * Look up a message key for a locale, falling back to the default catalog.
 * @param {string} key dotted path, e.g. `login.pitchBody`
 * @param {{ locale?: string|string[], values?: Object }} [options]
 * @returns {string}
 */
const t = (key, options = {}) => {
  const locale = getActiveLocale(options.locale);
  const primary = lookupPath(locales[locale], key);
  const fallback = locale === DEFAULT_LOCALE
    ? undefined
    : lookupPath(locales[DEFAULT_LOCALE], key);
  const template = typeof primary === 'string'
    ? primary
    : typeof fallback === 'string'
      ? fallback
      : key;
  return interpolate(template, options.values);
};

/**
 * Bound translator for a fixed locale (handy in tests).
 * @param {string|string[]} preferred
 * @returns {(key: string, values?: Object) => string}
 */
const createTranslator = (preferred) => {
  const locale = resolveLocale(preferred);
  return (key, values) => t(key, { locale, values });
};

module.exports = {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  LANGUAGE_DEFAULTS,
  REGION_BY_LANGUAGE,
  REGION_LOCALE,
  resolveLocale,
  detectBrowserLocales,
  getActiveLocale,
  t,
  createTranslator,
  interpolate
};
